import { IconButton } from "@circleco/compass/components/IconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { AnimatePresence, motion } from "framer-motion";
import type { Variants } from "framer-motion";

import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";
import { cn } from "../helpers/cn";
import { useCircleAiIntroAnimation } from "../hooks/useCircleAiIntroAnimation";
import { CircleAiDotsIcon } from "./CircleAiDotsIcon";

const dotsVariants: Variants = {
  hidden: { scale: 0, opacity: 0, transition: { duration: 0.3 } },
  visible: {
    scale: 1,
    opacity: 1,
    rotate: 360,
    transition: { duration: 0.3, rotate: { duration: 2.3, ease: "linear" } },
  },
};

const iconVariants: Variants = {
  hidden: { scale: 0, opacity: 0, transition: { duration: 0 } },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.3 } },
};

export const CommunitySwitcherCircleAiButton = () => {
  const { featureFlags, t } = useCommunitySwitcherContext();

  const showCircleAiButton = featureFlags?.showCircleAiButton;
  const shouldPlayIntro =
    showCircleAiButton === true &&
    featureFlags?.shouldShowCircleAiIntro === true;
  const {
    isDotsPhase,
    isIntroAnimating,
    isAnnouncing,
    setPhase,
    tooltipProps,
  } = useCircleAiIntroAnimation({
    shouldPlayIntro,
    onIntroShown: featureFlags?.onCircleAiIntroShown,
  });

  if (!showCircleAiButton) {
    return null;
  }

  const onCircleAiClick = featureFlags?.onCircleAiClick;

  return (
    <span
      className={cn("relative inline-flex", {
        "[&:hover_svg]:animate-[spin_4s_linear_infinite] motion-reduce:[&:hover_svg]:animate-none":
          !isIntroAnimating,
      })}
    >
      <motion.span
        className="inline-flex"
        variants={iconVariants}
        initial={false}
        animate={isDotsPhase ? "hidden" : "visible"}
      >
        <Tooltip
          content={t(
            isAnnouncing
              ? "community_switcher_circle_ai_lives_here"
              : "community_switcher_circle_ai",
          )}
          delay={200}
          side="right"
          sideOffset={12}
          {...tooltipProps}
        >
          <IconButton
            type="button"
            variant="ghost"
            icon="sparkle-filled"
            data-testid="circle-ai-shell-community-switcher-ai-toggle"
            data-circle-ai-toggle="community-switcher"
            aria-label={t("community_switcher_circle_ai")}
            onClick={onCircleAiClick}
          />
        </Tooltip>
      </motion.span>
      <AnimatePresence onExitComplete={() => setPhase("announcing")}>
        {isDotsPhase && (
          <motion.span
            key="dots"
            className="pointer-events-none absolute inset-0 m-auto inline-flex size-5"
            data-testid="circle-ai-shell-community-switcher-intro-dots"
            variants={dotsVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onAnimationComplete={definition => {
              if (definition === "visible") setPhase("icon");
            }}
            aria-hidden
          >
            <CircleAiDotsIcon />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
};
